import {
  IBackendSupportMessage,
  IBackendSupportSession,
  ISupportChatResponse,
  ISupportEscalationResult,
  ISupportSessionContextPayload,
  ISupportMessage,
  ISupportSession,
} from "../../types/support";
import { aiSupportApi } from "../api/ai-support.api";

/**
 * Клиент чата поддержки.
 *
 * До 2026-08-31 каждый метод этого файла имел «фолбэк»: при любой ошибке бэкенда
 * сервис молча подставлял локальную выдумку — сессию `session_<ts>`, ответ AI,
 * собранный по ключевым словам из двух зашитых статей, и оператора «Анна Петрова,
 * anna.petrova@example.com, рейтинг 4.8» с расписанием работы. Пользователь при
 * лежащем бэкенде видел работающую поддержку и ждал ответа несуществующего человека.
 * Заглушки в боевом пути запрещены каноном, поэтому фолбэков здесь больше нет:
 * ошибка бэкенда доходит до интерфейса, и интерфейс показывает отказ вместе с адресом
 * `SUPPORT_EMAIL` — каналом, который от AI-контура не зависит.
 *
 * Второе изменение — адресация. Раньше файл ходил через собственный raw-fetch клиент
 * `aiSupportApiService`, который не слал ни CSRF-заголовок, ни куки (значит, и не умел
 * обновлять протухший access-токен). Теперь все вызовы идут через `aiSupportApi`
 * поверх `httpClient`: SSOT адреса, токен из SecureStorage, CSRF и переигровка 401.
 */

/**
 * Бэкенд хранит сессию плоско (`currentPage`, `deviceInfo`, `aiConfidence`, `tags` —
 * колонки верхнего уровня), фронт работает с разложением `context`/`metadata`.
 * Раскладка делается здесь и явно: приведение типа скрыло бы, что половина полей
 * лежит не там, где их ищет интерфейс.
 */
const toSession = (be: IBackendSupportSession): ISupportSession => ({
  id: be.id,
  ...(be.userId != null && { userId: be.userId }),
  sessionType: be.sessionType,
  status: be.status,
  priority: be.priority,
  language: be.language,
  startTime: new Date(be.startTime),
  ...(be.endTime != null && { endTime: new Date(be.endTime) }),
  context: {
    ...(be.currentPage != null && { currentPage: be.currentPage }),
    ...(be.userAgent != null && { userAgent: be.userAgent }),
    ...(be.referrer != null && { referrer: be.referrer }),
    ...(be.lastActions != null && { lastActions: be.lastActions }),
    ...(be.eventId != null && { eventId: be.eventId }),
    ...(be.errorLogs != null && { errorLogs: be.errorLogs }),
    ...(be.sessionDuration != null && { sessionDuration: be.sessionDuration }),
    ...(be.deviceInfo != null && { deviceInfo: be.deviceInfo }),
  },
  metadata: {
    ...(be.aiConfidence != null && { aiConfidence: be.aiConfidence }),
    ...(be.escalationReason != null && {
      escalationReason: be.escalationReason,
    }),
    ...(be.tags != null && { tags: be.tags }),
    ...(be.sentiment != null && { sentiment: be.sentiment }),
    ...(be.urgency != null && { urgency: be.urgency }),
    ...(be.category != null && { category: be.category }),
    ...(be.subcategory != null && { subcategory: be.subcategory }),
    ...(be.userSatisfaction != null && {
      userSatisfaction: be.userSatisfaction,
    }),
  },
});

/** `timestamp` приезжает ISO-строкой — через JSON `Date` не переживает передачу. */
const toMessage = (be: IBackendSupportMessage): ISupportMessage => ({
  id: be.id,
  sessionId: be.sessionId,
  content: be.content,
  sender: be.sender,
  timestamp: new Date(be.timestamp),
  type: be.type,
  metadata: {
    ...(be.confidence != null && { confidence: be.confidence }),
    ...(be.intent != null && { intent: be.intent }),
    ...(be.entities != null && { entities: be.entities }),
    ...(be.suggestedActions != null && {
      suggestedActions: be.suggestedActions,
    }),
    ...(be.escalationSuggested != null && {
      escalationSuggested: be.escalationSuggested,
    }),
    ...(be.processingTime != null && { processingTime: be.processingTime }),
    ...(be.model != null && { model: be.model }),
    ...(be.operatorInfo != null && { operatorInfo: be.operatorInfo }),
  },
});

class SupportService {
  /**
   * Владелец сессии проставляется бэкендом из токена, поэтому userId сюда не
   * передаётся: в `CreateSupportSessionDto` такого поля нет, а глобальный
   * ValidationPipe с forbidNonWhitelisted отвечает на лишнее поле 400.
   */
  async createSession(
    context?: ISupportSessionContextPayload,
    language?: string,
  ): Promise<ISupportSession> {
    const session = await aiSupportApi.createSession({
      sessionType: "ai",
      priority: "medium",
      language: language ?? navigator.language.split("-")[0] ?? "ru",
      ...(context != null && { context }),
    });
    return toSession(session);
  }

  async sendMessage(
    sessionId: string,
    content: string,
  ): Promise<ISupportChatResponse> {
    const result = await aiSupportApi.sendMessage(sessionId, content);

    // Ответ AI лежит в `aiMessage`; поля `message` у бэкенда нет. Если ответа
    // нет — показывать нечего, и молчаливая подстановка «ответа» была бы именно
    // той выдумкой, ради снятия которой переписан этот файл.
    if (result?.aiMessage == null) {
      throw new Error("Бэкенд поддержки вернул ответ без сообщения AI");
    }

    const aiMessage = result.aiMessage;

    return {
      success: true,
      data: {
        message: toMessage(aiMessage),
        ...(result.suggestedActions != null && {
          suggestedActions: result.suggestedActions,
        }),
        ...(result.escalationSuggested != null && {
          escalationSuggested: result.escalationSuggested,
        }),
        ...(result.relatedArticles != null && {
          relatedArticles: result.relatedArticles,
        }),
      },
      metadata: {
        processingTime: aiMessage.processingTime ?? 0,
        requestId: aiMessage.id,
        ...(aiMessage.confidence != null && {
          confidence: aiMessage.confidence,
        }),
        ...(aiMessage.model != null && { model: aiMessage.model }),
      },
    };
  }

  /**
   * Возвращает ровно то, что отдаёт бэкенд: идентификатор и имя оператора, если
   * свободный нашёлся, и оценку времени ожидания. Оператора может не быть —
   * `findAvailableOperator` возвращает null; это штатный ответ, а не ошибка, и
   * интерфейс обязан его отличать от «оператор подключён».
   */
  async escalateToOperator(
    sessionId: string,
    reason: string = "user_request",
  ): Promise<ISupportEscalationResult> {
    return aiSupportApi.escalateSession(sessionId, reason);
  }

  async closeSession(
    sessionId: string,
    rating?: number,
    feedback?: string,
  ): Promise<void> {
    if (rating !== undefined) {
      await aiSupportApi.rateSession(sessionId, rating, feedback);
    }
    await aiSupportApi.closeSession(sessionId);
  }

  async rateSession(
    sessionId: string,
    rating: number,
    feedback?: string,
  ): Promise<void> {
    await aiSupportApi.rateSession(sessionId, rating, feedback);
  }
}

// Экспорт singleton instance
export const supportService = new SupportService();
export default SupportService;
